  import React, { useEffect, useMemo, useState } from 'react';
  import { MdClose, MdLogout } from 'react-icons/md';
  import { Spinner } from 'reactstrap';
  import Session from '../auth/Session';
  import { useFormContext } from '../../context/FormContext';
  import { formatCurrency } from '../util/formatters';
  import { authFetch, SessionExpiredError } from '../services/apiClient';
  import { uploadAdminDocument } from '../services/DocumentService';
  import { upsertReferredPolicy, finalizeReferredPolicy } from '../services/QuoteService';

  const TRIGGER_DISPLAY_NAMES = {
    single: 'Proxy Wind',
    dual: 'Proxy Wind + Anemometer',
    triple: 'Proxy Wind + Anemometer + Storm Track',
  };

  const INITIAL_UPLOAD_ROWS = [
    { id: 1, documentType: 'POLICY', file: null },
    { id: 2, documentType: 'INVOICE', file: null },
  ];

  function ConfirmModal({ onClose, onConfirm, saving }) {
    return (
      <div className="info-modal__overlay" onClick={saving ? undefined : onClose}>
        <div
          className="info-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm save"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="info-modal__header">
            <p className="info-modal__title">Confirm save</p>
            <button
              className="info-modal__close"
              onClick={onClose}
              aria-label="Close"
              disabled={saving}
            >
              <MdClose />
            </button>
          </div>
          <div className="info-modal__body">
            <p style={{ marginBottom: 'var(--space-md)' }}>
              Are you sure you want to save the policy information? Once saved, it cannot be edited.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                type="button"
                className="btn signup-cta btn--inverse-cta"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn signup-cta"
                onClick={onConfirm}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Confirm save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ReferredPolicy = ({ history }) => {
    const { formData, setFields, resetForm } = useFormContext();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    const [quote, setQuote] = useState(null);
    const [selectedDocumentIds, setSelectedDocumentIds] = useState({
      POLICY: null,
      INVOICE: null,
    });
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [uploadRows, setUploadRows] = useState(INITIAL_UPLOAD_ROWS);

    const [policyNumber, setPolicyNumber] = useState(formData.PolicyNumber || '');
    const [invoiceNumber, setInvoiceNumber] = useState(formData.InvoiceNumber || '');
    const [effectiveDate, setEffectiveDate] = useState(formData.EffectiveDate || '');
    const [expiryDate, setExpiryDate] = useState(formData.ExpiryDate || '');

    const quoteId = formData.QuoteId;

    const triggerLabel = useMemo(() => {
      const triggerType = quote?.selectedTriggerType || formData.SelectedTriggerType;
      return TRIGGER_DISPLAY_NAMES[triggerType] || triggerType || '-';
    }, [quote?.selectedTriggerType, formData.SelectedTriggerType]);

    const selectedPremium = useMemo(() => {
      const premium = quote?.selectedTotalPremium ?? formData.SelectedTotalPremium;
      return formatCurrency(premium);
    }, [quote?.selectedTotalPremium, formData.SelectedTotalPremium]);

    const quoteStatus = ((quote?.status || formData.Status || '') + '').trim().toLowerCase();
    const lockedForEdit = quoteStatus && quoteStatus !== 'referred';

    useEffect(() => {
      let cancelled = false;

      async function loadData() {
        if (!quoteId) {
          setError('No quote is loaded. Please return to Quote Dashboard and select a referred quote.');
          setLoading(false);
          return;
        }

        try {
          const response = await authFetch(`api/Quote/GetDashboardDataById/${quoteId}`);

          if (!response.ok) {
            setError('Unable to load quote details. Please try again.');
            setLoading(false);
            return;
          }

          const result = await response.json();
          if (!result.success || !result.data) {
            setError('Unable to load quote details. Please try again.');
            setLoading(false);
            return;
          }

          if (cancelled) return;

          const loadedQuote = result.data.quote || null;
          const docs = (result.data.documents || [])
            .filter((d) => ['POLICY', 'INVOICE'].includes((d.documentType || '').toUpperCase()));

          setQuote(loadedQuote);
          setExistingDocuments(docs);

          const latestPolicyDoc = docs.find((d) => (d.documentType || '').toUpperCase() === 'POLICY');
          const latestInvoiceDoc = docs.find((d) => (d.documentType || '').toUpperCase() === 'INVOICE');

          setSelectedDocumentIds({
            POLICY: latestPolicyDoc?.id || null,
            INVOICE: latestInvoiceDoc?.id || null,
          });

          setPolicyNumber(loadedQuote?.policyNumber || formData.PolicyNumber || '');
          setInvoiceNumber(loadedQuote?.invoiceNumber || formData.InvoiceNumber || '');
          setEffectiveDate(loadedQuote?.policyEffectiveDate || formData.EffectiveDate || '');
          setExpiryDate(loadedQuote?.policyExpiryDate || formData.ExpiryDate || '');
        } catch (err) {
          if (err instanceof SessionExpiredError) return;
          setError('Unable to load quote details. Please try again.');
        } finally {
          if (!cancelled) setLoading(false);
        }
      }

      loadData();
      return () => {
        cancelled = true;
      };
    }, [quoteId]);

    const handleUploadRowChange = (id, field, value) => {
      setUploadRows((prev) => prev.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      ));
      setValidationErrors((prev) => ({ ...prev, documents: null }));
    };

    const validate = () => {
      const errs = {};

      if (!policyNumber.trim()) errs.policyNumber = 'Policy number is required.';
      if (!invoiceNumber.trim()) errs.invoiceNumber = 'Invoice number is required.';
      if (!effectiveDate) errs.effectiveDate = 'Effective date is required.';
      if (!expiryDate) errs.expiryDate = 'Expiry date is required.';

      const effective = effectiveDate ? new Date(effectiveDate) : null;
      const expiry = expiryDate ? new Date(expiryDate) : null;
      if (effective && expiry && expiry <= effective) {
        errs.expiryDate = 'Expiry date must be after effective date.';
      }

      const filesByType = uploadRows
        .filter((r) => r.file)
        .reduce((acc, row) => {
          const type = (row.documentType || '').toUpperCase();
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});

      if ((filesByType.POLICY || 0) > 1 || (filesByType.INVOICE || 0) > 1) {
        errs.documents = 'Upload only one file per document type for each save.';
      }

      const hasPolicy = !!selectedDocumentIds.POLICY || (filesByType.POLICY || 0) === 1;
      const hasInvoice = !!selectedDocumentIds.INVOICE || (filesByType.INVOICE || 0) === 1;

      if (!hasPolicy || !hasInvoice) {
        errs.documents = 'Exactly one Policy and one Invoice document are required before saving.';
      }

      const invalidFile = uploadRows.find((r) => r.file && !r.file.name.toLowerCase().endsWith('.pdf'));
      if (invalidFile) {
        errs.documents = 'Only PDF documents can be uploaded.';
      }

      return errs;
    };

    const handleSaveClick = () => {
      setError('');
      const errs = validate();
      if (Object.keys(errs).length > 0) {
        setValidationErrors(errs);
        return;
      }
      setValidationErrors({});
      setShowConfirm(true);
    };

    const handleConfirmSave = async () => {
      if (!quoteId) {
        setError('No quote is loaded. Please return to Quote Dashboard and select a referred quote.');
        setShowConfirm(false);
        return;
      }

      setSaving(true);
      setError('');

      try {
        await upsertReferredPolicy({
          QuoteId: quoteId,
          PolicyNumber: policyNumber.trim(),
          InvoiceNumber: invoiceNumber.trim(),
          EffectiveDate: effectiveDate,
          ExpiryDate: expiryDate,
        });

        let policyDocumentId = selectedDocumentIds.POLICY || null;
        let invoiceDocumentId = selectedDocumentIds.INVOICE || null;

        for (const row of uploadRows) {
          if (!row.file) continue;

          const uploadResult = await uploadAdminDocument(quoteId, row.documentType, row.file);
          const uploaded = uploadResult.document;

          if (!uploaded?.id) {
            throw new Error('Document upload succeeded but did not return a document id.');
          }

          if ((row.documentType || '').toUpperCase() === 'POLICY') {
            policyDocumentId = uploaded.id;
          }
          if ((row.documentType || '').toUpperCase() === 'INVOICE') {
            invoiceDocumentId = uploaded.id;
          }
        }

        if (!policyDocumentId || !invoiceDocumentId) {
          throw new Error('Exactly one Policy and one Invoice document are required before saving.');
        }

        await finalizeReferredPolicy(quoteId);

        setFields({
          Status: 'Active',
          PolicyNumber: policyNumber.trim(),
          InvoiceNumber: invoiceNumber.trim(),
          EffectiveDate: effectiveDate,
          ExpiryDate: expiryDate,
        });

        setSelectedDocumentIds({
          POLICY: policyDocumentId,
          INVOICE: invoiceDocumentId,
        });

        setSuccess('Policy information saved successfully. Redirecting to dashboard...');
        setShowConfirm(false);

        setTimeout(() => {
          history.push('/dashboard');
        }, 900);
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        setError(err?.message || 'Unable to save policy information. Please try again.');
        setShowConfirm(false);
      } finally {
        setSaving(false);
      }
    };

    const handleLogout = () => {
      Session.removeUserSession();
      resetForm();
      window.location.href = '/login';
    };

    return (
      <div className="dashboard-shell">
        <header className="dashboard-header">
          {quoteId && (
            <button
              className="dashboard__logout"
              onClick={() => history.push('/dashboard')}
              style={{ marginRight: 'var(--space-md)' }}
            >
              Back to policy
            </button>
          )}
          <button className="dashboard__logout" onClick={handleLogout}>
            Log out
            <MdLogout size={16} style={{ marginLeft: '4px' }} />
          </button>
        </header>

        <main className="signup-card dashboard-card">
          <div className="signup-content">
            <h1 className="text-h1" style={{ marginTop: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
              Referred policy completion
            </h1>

            {loading && (
              <div className="dp-loading" aria-label="Loading referred policy...">
                <Spinner style={{ color: 'var(--brand-primary)' }} />
              </div>
            )}

            {!loading && error && (
              <p className="error-text" style={{ marginBottom: 'var(--space-md)' }}>
                {error}
              </p>
            )}

            {!loading && success && (
              <div className="eligibility-success" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="eligibility-success__text">
                  <strong>{success}</strong>
                </div>
              </div>
            )}

            {!loading && (
              <>
                {!quoteId ? (
                  <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                    <p className="text-default--primary" style={{ marginBottom: 'var(--space-sm)' }}>
                      Select a referred quote from Quote Dashboard to complete this workflow.
                    </p>
                    <button
                      type="button"
                      className="btn signup-cta"
                      onClick={() => history.push('/data/dashboard')}
                    >
                      Go to Quote Dashboard
                    </button>
                  </div>
                ) : lockedForEdit ? (
                  <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                    <p className="text-default--primary" style={{ marginBottom: 'var(--space-sm)' }}>
                      This quote is already {quote?.status || 'Active'} and cannot be edited from this page.
                    </p>
                    <button
                      type="button"
                      className="btn signup-cta"
                      onClick={() => history.push('/dashboard')}
                    >
                      Return to dashboard
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                        <p className="text-h4--brand" style={{ marginBottom: 'var(--space-md)' }}>Quote details</p>
                        <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="text-xs">Quote number</span>
                                <span className="text-default--primary">
                                    {quote?.quoteNumber || formData.QuoteNumber || '-'}
                                </span>
                            </div>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="text-xs">Data point option</span>
                                <span className="text-default--primary">{triggerLabel}</span>
                            </div>
                        </div>
                        <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="text-xs">Total annual premium</span>
                                <span className="text-default--primary">
                                    {quote?.selectedTotalPremium || formData.SelectedTotalPremium || '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                      <p className="text-h4--brand" style={{ marginBottom: 'var(--space-md)' }}>Policy details</p>

                      <div className="form-group">
                        <label className="text-label">Policy Number</label>
                        <input
                          type="text"
                          value={policyNumber}
                          onChange={(e) => {
                            setPolicyNumber(e.target.value);
                            setValidationErrors((prev) => ({ ...prev, policyNumber: null }));
                          }}
                          disabled={saving}
                        />
                        {validationErrors.policyNumber && (
                          <span className="error-text field-error">{validationErrors.policyNumber}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="text-label">Invoice Number</label>
                        <input
                          type="text"
                          value={invoiceNumber}
                          onChange={(e) => {
                            setInvoiceNumber(e.target.value);
                            setValidationErrors((prev) => ({ ...prev, invoiceNumber: null }));
                          }}
                          disabled={saving}
                        />
                        {validationErrors.invoiceNumber && (
                          <span className="error-text field-error">{validationErrors.invoiceNumber}</span>
                        )}
                      </div>

                      <div className="summary-grid" style={{ marginBottom: 0 }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="text-label" style={{ marginRight: 'var(--space-sm)' }}>Effective Date</label>
                          <input
                            type="date"
                            value={effectiveDate}
                            onChange={(e) => {
                              setEffectiveDate(e.target.value);
                              setValidationErrors((prev) => ({ ...prev, effectiveDate: null }));
                            }}
                            disabled={saving}
                          />
                          {validationErrors.effectiveDate && (
                            <>
                                <br />
                                <span className="error-text field-error">{validationErrors.effectiveDate}</span>
                            </>
                          )}
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="text-label" style={{ marginRight: 'var(--space-sm)' }}>Expiry Date</label>
                          <input
                            type="date"
                            value={expiryDate}
                            onChange={(e) => {
                              setExpiryDate(e.target.value);
                              setValidationErrors((prev) => ({ ...prev, expiryDate: null }));
                            }}
                            disabled={saving}
                          />
                          {validationErrors.expiryDate && (
                            <>
                                <br />
                                <span className="error-text field-error">{validationErrors.expiryDate}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                      <p className="text-h4--brand" style={{ marginBottom: 'var(--space-md)' }}>Required documents</p>
                      <p className="signup-section__desc" style={{ marginBottom: 'var(--space-md)' }}>
                        Upload exactly one Policy PDF and one Invoice PDF.
                      </p>

                        
                      {/* <div className="summary-row" style={{ marginBottom: 'var(--space-md)' }}>
                        <span className="text-xs">Current Policy document</span>
                        <span className="text-default--primary">
                          {existingDocuments.find((d) => (d.documentType || '').toUpperCase() === 'POLICY')?.fileName || '-'}
                        </span>
                      </div>

                      <div className="summary-row" style={{ marginBottom: 'var(--space-md)' }}>
                        <span className="text-xs">Current Invoice document</span>
                        <span className="text-default--primary">
                          {existingDocuments.find((d) => (d.documentType || '').toUpperCase() === 'INVOICE')?.fileName || '-'}
                        </span>
                      </div> */}

                      {uploadRows.map((row) => (
                          <div className="summary-grid" key={row.id} style={{ marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'flex-end' }}>
                              <div className="form-group" style={{ marginBottom: 0, marginRight: 'var(--space-lg)' }}>
                                  <label className="text-label">Document Type</label>
                                  <div
                                      className="text-default--primary"
                                      style={{
                                      display: 'flex',
                                      alignItems: 'center'
                                      }}
                                  >
                                      {row.documentType === 'POLICY' ? 'Policy' : 'Invoice'}
                                  </div>
                              </div>

                              <div className="form-group" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                                  {/* <label className="text-label">PDF File</label> */}
                                  <input
                                      type="file"
                                      accept=".pdf,application/pdf"
                                      onChange={(e) => handleUploadRowChange(row.id, 'file', e.target.files?.[0] || null)}
                                      disabled={saving}
                                  />
                              </div>
                          </div>
                      ))}

                      {validationErrors.documents && (
                          <span className="error-text field-error">{validationErrors.documents}</span>
                      )}
                    </div>

                    <div className="signup-nav">
                        <button className="btn signup-cta btn--inverse-cta" onClick={() => history.push('/dashboard')} disabled={saving}>
                            Cancel
                        </button>
                        <button className="btn signup-cta" onClick={handleSaveClick} disabled={saving}>
                            {saving ? 'Saving...' : 'Save policy'}
                        </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </main>

        <footer className="signup-footer" style={{ marginTop: 'var(--space-lg)' }}>
          <p>Need assistance?</p>
          <p>Call us at <a href="tel:8772695422">877-269-5422</a> to connect.</p>
        </footer>

        {showConfirm && (
          <ConfirmModal
            onClose={() => setShowConfirm(false)}
            onConfirm={handleConfirmSave}
            saving={saving}
          />
        )}
      </div>
    );
  };

  export default ReferredPolicy;